import {
  Card,
  CardContent,
  CardHeader,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { useDocumentViewer } from '@hooks/useDocumentViewer';
import { useIsMobile } from '@hooks/useIsMobile';

import type { Document } from '@models/document';

import { formatFilename } from '@utils/format';

const messages = defineMessages({
  noPreview: {
    id: 'documentViewer.noPreview',
    defaultMessage: 'Unable to preview file',
  },
});

const DocumentViewer: React.FC<{
  doc: Document;
  open: boolean;
}> = ({ doc, open }) => {
  const { formatMessage } = useIntl();
  const mobile = useIsMobile();
  const { reset } = useDocumentViewer();

  const { downloadUrl, name } = doc;
  const filename = formatFilename(name);

  const handleClose = () => reset();

  const renderImgPreview = (uri: string, name: string) => (
    <img alt={name} src={uri} />
  );

  const renderPdfPreview = (uri: string, name: string) => (
    <iframe height="100%" src={uri} title={name} width="100%" />
  );

  const children = useMemo(() => {
    const extension = name?.split('.').pop()?.toLowerCase();
    const url = downloadUrl?.url ?? '';
    const filename = name ?? '';

    switch (extension) {
      case 'png':
      case 'jpg':
      case 'jpeg':
        return renderImgPreview(url, filename);
      case 'pdf':
        return renderPdfPreview(url, filename);
      default:
        return (
          <Typography variant="body2">
            {formatMessage(messages.noPreview)}
          </Typography>
        );
    }
  }, [downloadUrl, formatMessage, name]);

  const { width, height } = useMemo(() => {
    let width = 10;
    let height = 10;
    if (typeof window !== 'undefined') {
      width = window.innerWidth * 0.1;
      height = window.innerHeight * 0.1;
    }
    return { width, height };
  }, []);

  return (
    <Dialog
      fullScreen={mobile}
      PaperProps={{
        sx: {
          width: mobile ? '100%' : `calc(100% - ${width}px)`,
          height: mobile ? '100%' : `calc(100% - ${height}px)`,
        },
      }}
      maxWidth="xl"
      onClose={handleClose}
      open={open}
    >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardHeader
          action={
            <Icon
              clickable
              color="text.primary"
              name="xmark"
              onClick={handleClose}
              size="lg"
              style={{
                display: 'flex',
                alignContent: 'center',
                marginRight: '5px',
              }}
              type="fas"
            />
          }
          title={<Typography>{filename}</Typography>}
        />
        <Divider />
        <CardContent
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            overflow: 'scroll',
            p: 0,
          }}
        >
          {children}
        </CardContent>
      </Card>
    </Dialog>
  );
};

export default DocumentViewer;
